/* global fetch */

import { config } from '../config';
import { APIResponse, fetchAPI, headers, SpanTag, stleaf } from '../helpers';

export type GlobalSearchResultType = 'map' | 'mission' | 'dataset';

export interface GlobalSearchResult {
  type: GlobalSearchResultType;
  id: string;
  name: string;
  workspaceId: string;
  workspaceName: string | null;
  /** Parent map for dataset results; null for maps/missions. */
  mapId: string | null;
  layerType: string | null;
  thumbnailUrl: string | null;
  createdAt: string;
  /** Storage behind the item in bytes; null when it has no media. */
  sizeBytes: number | null;
  center: [number, number] | null;
  bbox: [number, number, number, number] | null;
  score: number;
}

export type GlobalSearchBounds = [[number, number], [number, number]];

export interface GlobalSearchResponse {
  results: GlobalSearchResult[];
  bounds: GlobalSearchBounds | null;
  hasMore: boolean;
}

export interface GlobalSearchPin {
  type: 'map' | 'mission';
  id: string;
  name: string;
  workspaceId: string;
  center: [number, number];
  createdAt: string;
}

export interface GlobalSearchPinsResponse {
  pins: GlobalSearchPin[];
}

export const search = (
  st: SpanTag,
  q: string,
  opts?: {
    limit?: number;
    offset?: number;
    /** Viewport filter: [minLng, minLat, maxLng, maxLat] */
    bbox?: [number, number, number, number];
    /** Restrict to one of the user's workspaces. */
    workspaceId?: string;
    /** Map search was opened from; always first on page one. */
    pinMapId?: string;
    /** One entity type only (the UI pages each type independently). */
    type?: GlobalSearchResultType;
    signal?: AbortSignal;
  },
): Promise<APIResponse<GlobalSearchResponse>> => {
  const url = new URL(`${config.API_URL}/v2/search`);
  // Empty q = browse mode (maps by recency).
  if (q) url.searchParams.set('q', q);
  if (opts?.limit) url.searchParams.set('limit', String(opts.limit));
  if (opts?.offset) url.searchParams.set('offset', String(opts.offset));
  if (opts?.bbox) url.searchParams.set('bbox', opts.bbox.join(','));
  if (opts?.workspaceId) url.searchParams.set('workspaceId', opts.workspaceId);
  if (opts?.pinMapId) url.searchParams.set('pinMapId', opts.pinMapId);
  if (opts?.type) url.searchParams.set('type', opts.type);
  return fetchAPI(stleaf(st, 'gsrch'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
    ...(opts?.signal ? { signal: opts.signal } : {}),
  }) as Promise<APIResponse<GlobalSearchResponse>>;
};

export const getPins = (
  st: SpanTag,
  opts?: { workspaceId?: string },
): Promise<APIResponse<GlobalSearchPinsResponse>> => {
  const url = new URL(`${config.API_URL}/v2/search/pins`);
  if (opts?.workspaceId) url.searchParams.set('workspaceId', opts.workspaceId);
  return fetchAPI(stleaf(st, 'gpins'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as Promise<APIResponse<GlobalSearchPinsResponse>>;
};

export interface GlobalSearchMapDatasetsResponse {
  results: GlobalSearchResult[];
  hasMore: boolean;
}

/** Every dataset layer of one map (for the "Selected Map Datasets" tile). */
export const getMapDatasets = (
  st: SpanTag,
  mapId: string,
  opts?: { limit?: number; offset?: number; signal?: AbortSignal },
): Promise<APIResponse<GlobalSearchMapDatasetsResponse>> => {
  const url = new URL(`${config.API_URL}/v2/search/maps/${mapId}/datasets`);
  if (opts?.limit) url.searchParams.set('limit', String(opts.limit));
  if (opts?.offset) url.searchParams.set('offset', String(opts.offset));
  return fetchAPI(stleaf(st, 'gmpds'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
    ...(opts?.signal ? { signal: opts.signal } : {}),
  }) as Promise<APIResponse<GlobalSearchMapDatasetsResponse>>;
};

/** Zips the item's files and emails the user a download link. */
export const requestDownload = (
  st: SpanTag,
  type: GlobalSearchResultType,
  id: string,
): Promise<APIResponse<{ ok: boolean; count: number }>> => {
  const url = `${config.API_URL}/v2/search/${type}/${id}/download`;
  return fetchAPI(stleaf(st, 'gsdl'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
  }) as Promise<APIResponse<{ ok: boolean; count: number }>>;
};
